import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useTranslation } from 'react-i18next'
import SectionHeader from './SectionHeader'
import StatusPill from './StatusPill'
import type { NeedsAttentionItem } from './dashboardTypes'

type NeedsAttentionCardProps = {
  items: NeedsAttentionItem[]
  onItemAction: (item: NeedsAttentionItem) => void
  onShowAll?: () => void
}

export default function NeedsAttentionCard({ items, onItemAction, onShowAll }: NeedsAttentionCardProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: `${theme.affexy.radii.lg / 1.33}px`,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
      }}
    >
      <SectionHeader
        title={t('needsAttention.title')}
        subtitle={t('needsAttention.subtitle')}
        right={
          <Button color="inherit" sx={{ color: c.ink72, fontSize: 13 }} onClick={onShowAll}>
            {t('needsAttention.showAll')}
          </Button>
        }
      />

      {items.length === 0 && (
        <Typography sx={{ fontSize: 13, color: c.ink56, py: 1 }}>{t('needsAttention.empty')}</Typography>
      )}

      <Stack spacing={1.25}>
        {items.map((it) => {
          const isReview = it.status === 'review'
          return (
            <Box
              key={it.id}
              sx={{
                p: '14px 16px',
                border: `1px solid ${c.ink08}`,
                borderRadius: `${theme.affexy.radii.md - 2}px`,
                display: 'flex',
                alignItems: 'center',
                gap: 1.75,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: theme.affexy.fonts.heading,
                      fontWeight: 700,
                      fontSize: 14,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {it.name}
                  </Typography>
                  <StatusPill kind={it.status} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, fontSize: 12.5, color: c.ink56 }}>
                  <span>{t(`needsAttention.reason.${it.reasonKey}`)}</span>
                  <Box component="span" sx={{ color: c.ink32 }}>
                    ·
                  </Box>
                  <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {it.detail}
                  </Box>
                </Box>
              </Box>
              <Button
                variant={isReview ? 'contained' : 'outlined'}
                size="small"
                onClick={() => onItemAction(it)}
                endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {t(`needsAttention.action.${it.actionKey}`)}
              </Button>
            </Box>
          )
        })}
      </Stack>
    </Paper>
  )
}
